import React from "react";
import DOMPurify from "dompurify";
import { chatMarkdownToHtml, parseChatMarkdown } from "@toolkit/mobile-shared";
import { Bot, Send } from "lucide-react";
import { cn } from "../../lib/utils";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import {
  ChatAssistantMessageRow,
  ChatMarkdownBubble,
  ChatUserMessageRow,
} from "./ChatMessageRow";

export type ChatMessage = {
  role: "user" | "assistant" | "system" | "error";
  content: string;
  // Optional structured payload attached to a message (e.g. design updates)
  metadata?: Record<string, unknown>;
};

const MARKDOWN_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "code",
  "pre",
  "blockquote",
];

export const formatChatMessage = (text: string) =>
  text
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");

const formatAndSanitizeChatMessage = (text: string) => {
  const rawHtml = chatMarkdownToHtml(parseChatMarkdown(text));
  if (typeof window === "undefined") {
    return rawHtml;
  }
  return DOMPurify.sanitize(rawHtml || "", {
    ALLOWED_TAGS: MARKDOWN_ALLOWED_TAGS,
    ALLOWED_ATTR: [],
  });
};

const MAX_TEXTAREA_ROWS = 6;

/** Matches dashboard / .ds-container horizontal rhythm (mobile-first). */
const CHAT_SECTION_GUTTER_X = "px-4 sm:px-6";

type ChatInterfaceProps = {
  messages: ChatMessage[];
  input: string;
  onInputChange: (value: string) => void;
  onSend: (event: React.FormEvent) => void;
  isProcessing: boolean;
  agentError?: string | null;
  errorTitle?: string;
  placeholder?: string;
  pendingActionPlaceholder?: string;
  header?: { title: string; subtitle: string };
  /** Extra element rendered inline in the header (e.g. toggle pill). */
  headerExtra?: React.ReactNode;
  inputPrefix?: React.ReactNode;
  renderPendingAction?: () => React.ReactNode;
  /** When set, replaces the input form with custom actions (e.g. Confirm/Skip for selectors). Keeps action buttons in a consistent bottom bar location for better UX. */
  bottomBarAction?: React.ReactNode;
  containerClassName?: string;
  messagesClassName?: string;
  /** Optional extra content rendered below a specific message bubble. */
  renderMessageSuffix?: (msg: ChatMessage, index: number) => React.ReactNode;
  /** Rendered as the first item in the messages list (before dynamic messages). */
  welcomeCard?: React.ReactNode;
  /** URL or data-uri for the current user's avatar. Falls back to a generic icon. */
  userAvatar?: string | null;
  /** Display name for the current user (shown above user bubbles). */
  userName?: string;
};

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  input,
  onInputChange,
  onSend,
  isProcessing,
  agentError,
  errorTitle = "AI assistant unavailable",
  placeholder = "Type your message...",
  pendingActionPlaceholder = "Or type a response...",
  header,
  headerExtra,
  inputPrefix,
  renderPendingAction,
  bottomBarAction,
  containerClassName,
  messagesClassName = "h-112 space-y-6 overflow-y-auto p-4",
  renderMessageSuffix,
  welcomeCard,
  userAvatar,
  userName,
}) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const prevProcessingRef = React.useRef(isProcessing);
  const pendingActionContent = renderPendingAction?.();

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isProcessing]);

  React.useEffect(() => {
    if (prevProcessingRef.current && !isProcessing) {
      textareaRef.current?.focus();
    }
    prevProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Auto-resize textarea on input change
  React.useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 20;
    const maxHeight = lineHeight * MAX_TEXTAREA_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, [input]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className={containerClassName}>
      {header ? (
        <div
          data-tour="dashboard-chat-header"
          className={cn(
            "shrink-0 border-b border-border bg-muted/40 py-4 backdrop-blur",
            CHAT_SECTION_GUTTER_X,
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Bot className="w-5 h-5 text-premium-500" />
              {header.title}
            </h2>
            {headerExtra}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {header.subtitle}
          </p>
        </div>
      ) : null}

      {agentError ? (
        <Alert
          variant="error"
          className={cn("shrink-0", header ? "m-4" : "mb-6")}
        >
          <p className="font-medium">{errorTitle}</p>
          <AlertDescription>{agentError}</AlertDescription>
        </Alert>
      ) : null}

      <div className={messagesClassName}>
        {/* Welcome card rendered before dynamic messages */}
        {welcomeCard ? (
          <ChatAssistantMessageRow>{welcomeCard}</ChatAssistantMessageRow>
        ) : null}

        {messages.map((msg, index) => (
          <div key={index}>
            {msg.role === "user" ? (
              <ChatUserMessageRow userAvatar={userAvatar} userName={userName}>
                <ChatMarkdownBubble
                  variant="user"
                  html={formatAndSanitizeChatMessage(msg.content)}
                />
              </ChatUserMessageRow>
            ) : msg.role === "assistant" ? (
              <ChatAssistantMessageRow>
                <ChatMarkdownBubble
                  variant="assistant"
                  html={formatAndSanitizeChatMessage(msg.content)}
                />
              </ChatAssistantMessageRow>
            ) : msg.role === "error" ? (
              <div className="flex justify-start">
                <div
                  className="max-w-[80%] rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive [&_h2]:text-base [&_ul]:my-1 [&_li]:ml-3"
                  dangerouslySetInnerHTML={{
                    __html: formatAndSanitizeChatMessage(msg.content),
                  }}
                />
              </div>
            ) : (
              // system messages
              <div className="flex justify-start">
                <div
                  className="max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground [&_h2]:text-base [&_ul]:my-1 [&_li]:ml-3"
                  dangerouslySetInnerHTML={{
                    __html: formatAndSanitizeChatMessage(msg.content),
                  }}
                />
              </div>
            )}
            {renderMessageSuffix?.(msg, index)}
          </div>
        ))}

        {pendingActionContent}

        {isProcessing ? (
          <ChatAssistantMessageRow contentColumnClassName="space-y-1 max-w-[90%]">
            <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-2.5 shadow-sm">
              <div className="flex gap-1 items-center h-5">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50 animate-bounce" />
              </div>
            </div>
          </ChatAssistantMessageRow>
        ) : null}

        <div ref={messagesEndRef} />
      </div>

      <div
        data-tour="dashboard-chat-input"
        className={cn(
          "shrink-0 border-t border-border bg-muted/50 py-3 sm:py-4 max-w-full min-w-0 overflow-x-hidden",
          CHAT_SECTION_GUTTER_X,
        )}
      >
        {bottomBarAction ? (
          <div className="flex items-center gap-2">{bottomBarAction}</div>
        ) : (
          <form
            onSubmit={onSend}
            className={cn(
              "flex w-full min-w-0 items-center gap-2 min-h-[44px]",
              inputPrefix ? "gap-3" : "gap-2",
            )}
          >
            {inputPrefix}
            <textarea
              ref={textareaRef}
              rows={1}
              value={input}
              onChange={(e) => onInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 resize-none overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50 min-h-[40px] leading-normal md:min-h-[36px]"
              placeholder={
                pendingActionContent ? pendingActionPlaceholder : placeholder
              }
              disabled={!!agentError || isProcessing}
              autoFocus
            />
            <Button
              type="submit"
              variant="premium"
              className="disabled:opacity-50 shrink-0 self-end"
              disabled={!!agentError || isProcessing}
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
